import { autoinject, bindable, computedFrom, observable } from 'aurelia-framework';
import { MeasureService, IMeasureInputAM, IMeasureEntryAM, MeasureEntryAM, IMeasureInputRuleReferenceAM } from '../../services/measure-service';
import { CustomerMunicipality } from '../../../municipalities/municipality-service';
import { CustomerMunicipalityWithServiceInvolvementField } from './measure-input-group-view';
import { IFormItem } from '../../measure-input-group';
import { ResourceService } from '../../../../utils/resourceService';

@autoinject
export class MeasureInputChart {

	@bindable customerMunicipalities: CustomerMunicipality[];
	@bindable partialMemberServiceSelections: { [customerMunicipalityId: string]: string[] } = { }; // key: customerMunicipalityId, value: selected service-id's

	@bindable measureInputs: IMeasureInputAM[];
	@bindable insertMeasureValueCb: (params: any) => void;
	@bindable findMeasureEntry: (params: any) => MeasureEntryAM;
	@bindable findComparisonMeasureEntry: (params: any) => MeasureEntryAM;
	@bindable showComparisonValues : boolean;
	@bindable readonly: boolean;

	@bindable formItem: IFormItem;
	@bindable groupTitle: string;

	constructor(private measureService: MeasureService, private resourceService: ResourceService) {
	}

	@computedFrom('measureInputs', 'formItem')
	get showSumColumn(): boolean {
		return this.measureInputs && this.measureInputs.filter(i => this.showSummaryFieldFor(i)).length > 0;
	}

	@computedFrom('measureInputs', 'formItem')
	get showMunicipalityColumns(): boolean {
		return this.measureInputs && this.measureInputs.filter(i => this.showMunicipalityFieldsFor(i)).length > 0;
	}

	@computedFrom('formItem')
	get itemTitle(): string {
		return this.formItem.showInputsForServiceLevel ?
			this.resourceService.getResource("Measures_Heading_ForWholeService") :
			this.formItem.title;
	}
		
	// Normal customer municipalities + those partial members that are involved in this service + non-involved partial members that have inputs
	@computedFrom('formItem', 'customerMunicipalities', 'partialMemberServiceSelections')
	get customerMunicipalitiesToShow(): CustomerMunicipalityWithServiceInvolvementField[] {
	
		const partialMembers = this.customerMunicipalities.filter(cm => cm.type === "PartialMember");
		const customerMunicipalityIdsNotInvolvedInThisService =
			partialMembers
				.filter(pm => this.partialMemberServiceSelections[pm.customerMunicipalityId].indexOf(this.formItem.serviceId) < 0)
				.map(pm => pm.customerMunicipalityId);

		const customerMunicipalitiesWithServiceInvolemntField = this.customerMunicipalities.map(cm => {
			let x = new CustomerMunicipalityWithServiceInvolvementField;
			Object.assign(x, cm);
			x.IsInvolvedInCurrentService = customerMunicipalityIdsNotInvolvedInThisService.indexOf(cm.customerMunicipalityId) < 0;
			return x;
		});

		return customerMunicipalitiesWithServiceInvolemntField.filter(cm =>
			cm.IsInvolvedInCurrentService ||
			!!this.measureInputs.find(i =>
				!!i.existingEntries.find(e =>
					e.customerMunicipalityId === cm.customerMunicipalityId &&
					((e.costCenterId == this.formItem.costCenterId) || (!e.costCenterId))
				)
			)
		);
	}

	showSummaryFieldFor(measureInput: IMeasureInputAM) {
		if (this.formItem.serviceId && this.formItem.costCenterId) {
			return measureInput.forCostCentersTotal || measureInput.existingEntries.some(me => !!me.costCenterId && !me.customerMunicipalityId);
		}
		return measureInput.forServiceTotal || measureInput.existingEntries.some(me => !me.costCenterId && !me.customerMunicipalityId);
	}

	showMunicipalityFieldsFor(measureInput: IMeasureInputAM) {
		if (this.formItem.serviceId && this.formItem.costCenterId) {
			return measureInput.forCostCentersByMunicipalities || measureInput.existingEntries.some(me => !!me.costCenterId && !!me.customerMunicipalityId);
		}
		return measureInput.forServiceByMunicipalities || measureInput.existingEntries.some(me => !me.costCenterId && !!me.customerMunicipalityId);
	}

	insertMeasureValue(measureEntry: IMeasureEntryAM, value: string) {
		this.insertMeasureValueCb({ measureEntry, value });
	}

	getMeasureEntryForInput(
		measureId: string,
		customerMunicipalityId: string = null,
		serviceId: string,
		costCenterId: string = null,
		dimensionValueIds: string[] = null): MeasureEntryAM {

		return this.findMeasureEntry({
			measureId,
			customerMunicipalityId,
			serviceId,
			costCenterId,
			dimensionValueIds
		});
	}

	getComparisonMeasureEntryForInput(
		measureId: string,
		customerMunicipalityId: string = null,
		serviceId: string,
		costCenterId: string = null,
		dimensionValueIds: string[] = null): MeasureEntryAM {

		return this.findComparisonMeasureEntry({
			measureId,
			customerMunicipalityId,
			serviceId,
			costCenterId,
			dimensionValueIds
		});
	}

	getRuleReferenceInfo(measureInput: IMeasureInputAM): IMeasureInputRuleReferenceAM {
		
		if (!measureInput.ruleReferences || !measureInput.ruleReferences.length || !this.formItem) {
			return null;
		}
		
		const dimensionValueId = this.formItem.dimensionValueIds && this.formItem.dimensionValueIds.length
			? this.formItem.dimensionValueIds[0]
			: null;
		
		const ruleReferenceInfo = measureInput.ruleReferences.find(rr =>
			rr.costCenterId === this.formItem.costCenterId &&
			rr.dimensionValueId === dimensionValueId);

		return ruleReferenceInfo;
	}
}
